import { AlertTriangle, CheckCircle2, Percent, Receipt } from 'lucide-react';

import ChartCard from '@/features/reports/components/ChartCard';
import OrderFunnelChart, { STATUS_COLORS } from '@/features/reports/components/charts/OrderFunnelChart';
import { SimpleKpi } from '@/features/reports/components/KpiTile';
import ReportTable from '@/features/reports/components/ReportTable';
import { useReportOutlet } from '@/features/reports/hooks/useReportOutlet';
import { useOrderFunnel } from '@/features/reports/hooks/useReports';
import {
  formatDecimal,
  formatNumber,
  formatPercent,
  formatRangeCaption,
  formatToman,
} from '@/features/reports/lib/format';
import { STATUS_LABELS } from '@/features/reports/lib/filters';
import { getPersianLabel } from '@/lib/persianUi';

/** Order pipeline: how many orders sit in each status, and how many completed. */
export default function OrdersFunnelReport() {
  const { query, filters } = useReportOutlet();
  const funnel = useOrderFunnel(query);

  const data = funnel.data;
  const rows = data?.rows ?? [];

  return (
    <div className="reports-page">
      <div className="report-section-head">
        <h2 className="section-title">قیف سفارش‌ها</h2>
        <p className="report-section-caption">
          {formatRangeCaption(filters.from, filters.to)} • همه وضعیت‌ها، از جمله سفارش‌های ناموفق
        </p>
      </div>

      <p className="report-note">
        این گزارش فیلتر وضعیت را نادیده می‌گیرد؛ قیف تنها زمانی معنا دارد که همه وضعیت‌ها با هم دیده
        شوند. فیلترهای بازه زمانی اعمال شده‌اند.
      </p>

      <div className="kpi-grid report-kpi-grid">
        <SimpleKpi
          title="کل سفارش‌ها"
          value={data?.total_orders}
          format={formatNumber}
          icon={Receipt}
          accent="blue"
        />
        <SimpleKpi
          title="سفارش‌های تحقق‌یافته"
          value={data?.fulfilled_orders}
          format={formatNumber}
          icon={CheckCircle2}
          accent="green"
        />
        <SimpleKpi
          title="سفارش‌های ناموفق"
          value={data?.failed_orders}
          format={formatNumber}
          icon={AlertTriangle}
          accent="red"
        />
        <SimpleKpi
          title="نرخ تحقق"
          value={data?.fulfillment_rate_pct}
          format={(value) => formatPercent(value)}
          icon={Percent}
          accent="green"
        />
        <SimpleKpi
          title="نرخ شکست"
          value={data?.failure_rate_pct}
          format={(value) => formatPercent(value)}
          icon={AlertTriangle}
          accent="red"
        />
      </div>

      <div className="analytics-grid">
        <ChartCard
          title="قیف وضعیت سفارش‌ها"
          subtitle="پهنای هر نوار نسبت به کل سفارش‌های همان بازه است"
          height={340}
          isLoading={funnel.isLoading}
          isError={funnel.isError}
          error={funnel.error}
          isEmpty={!rows.length}
        >
          <OrderFunnelChart rows={rows} height={340} />
        </ChartCard>

        <ChartCard
          title="توزیع وضعیت‌ها"
          subtitle="سهم هر وضعیت از کل سفارش‌ها"
          height={340}
          isLoading={funnel.isLoading}
          isError={funnel.isError}
          error={funnel.error}
          isEmpty={!rows.length}
        >
          <ul className="report-status-list">
            {rows.map((row) => (
              <li key={row.status} className="report-status-item">
                <span
                  className="report-status-dot"
                  style={{ background: STATUS_COLORS[row.status] ?? '#546E7A' }}
                  aria-hidden="true"
                />
                <span className="report-status-name">{getPersianLabel(STATUS_LABELS, row.status, 'reports.deliveryStatus')}</span>
                <span className="report-status-bar" aria-hidden="true">
                  <span
                    className="report-status-fill"
                    style={{
                      width: `${Math.min(100, Number(row.share_pct ?? 0))}%`,
                      background: STATUS_COLORS[row.status] ?? '#546E7A',
                    }}
                  />
                </span>
                <span className="report-status-count">{formatNumber(row.count)}</span>
                <span className="report-status-share">{formatPercent(row.share_pct)}</span>
              </li>
            ))}
          </ul>
        </ChartCard>
      </div>

      <ChartCard
        title="جدول وضعیت‌ها"
        subtitle="تعداد و مبلغ سفارش‌های هر وضعیت"
        bodyClassName="report-card-body-table"
        isLoading={funnel.isLoading}
        isError={funnel.isError}
        error={funnel.error}
        isEmpty={!rows.length}
      >
        <ReportTable
          rows={rows}
          rowKey={(row) => row.status}
          columns={[
            {
              key: 'label',
              header: 'وضعیت',
              render: (row) => (
                <span className="report-status-inline">
                  <span
                    className="report-status-dot"
                    style={{ background: STATUS_COLORS[row.status] ?? '#546E7A' }}
                    aria-hidden="true"
                  />
                  {getPersianLabel(STATUS_LABELS, row.status, 'reports.deliveryStatus')}
                </span>
              ),
            },
            { key: 'count', header: 'تعداد', render: (row) => formatNumber(row.count) },
            { key: 'share_pct', header: 'سهم', render: (row) => formatPercent(row.share_pct) },
            { key: 'revenue', header: 'مبلغ', render: (row) => formatToman(row.revenue) },
            {
              key: 'share_bar',
              header: 'نمودار',
              render: (row) => (
                <span className="report-status-bar" aria-hidden="true">
                  <span
                    className="report-status-fill"
                    style={{
                      width: `${Math.min(100, Number(row.share_pct ?? 0))}%`,
                      background: STATUS_COLORS[row.status] ?? '#546E7A',
                    }}
                  />
                </span>
              ),
            },
          ]}
        />
      </ChartCard>

      {data && (
        <p className="report-footnote">
          نرخ تحقق: {formatDecimal(data.fulfillment_rate_pct, 1)}٪ • نرخ شکست:{' '}
          {formatDecimal(data.failure_rate_pct, 1)}٪
        </p>
      )}
    </div>
  );
}
